import React, { Suspense, useMemo } from 'react';
import {
  PopoverBody,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import Points from '@/_components/Points';
import { DownIcon } from '@/_assets/icon-components';

import MarkerPin2Icon from '@/_assets/icon-components/MarkerPin2';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { Box, HStack, Text, useToken, VStack } from '@chakra-ui/react';
import { onOpenMap, savePoint } from '../../_store/common/common.slice';
import { sessionActions } from '../../_store/session/session.slice';
import { IRedux } from '../../_store/types';
import { IPoint } from '../../_store/common/types';

interface IProps {
  open: boolean;
  activePoint: IPoint | null;
  setOpen: (e: boolean) => void;
}

export default function Desktop({ open, activePoint, setOpen }: IProps) {
  const t = useTranslations();
  const { permissions } = useSelector((state: IRedux) => state.session);

  const primary = useToken('colors', 'primary');
  const primarySubtle = useMemo(
    () => (primary[0].length > 4 ? primary[0] + '14' : primary[0] + '1'),
    [primary]
  );
  return (
    <PopoverRoot open={open} onOpenChange={(e) => setOpen(e.open)}>
      <PopoverTrigger asChild>
        <Button
          gap={2}
          size='xl'
          h={'48px'}
          rounded={16}
          variant='plain'
          display={'flex'}
          alignItems={'center'}
          bg={open ? primarySubtle : 'light.300'}
          borderColor={open ? 'primary' : 'transparent'}
          _hover={{
            bgColor: primarySubtle,
            borderColor: 'primary',
          }}
          _active={{
            bgColor: primarySubtle,
            borderColor: 'primary',
          }}
        >
          <Text as={'span'} color={'primary'}>
            <MarkerPin2Icon />
          </Text>
          <Suspense fallback='Укажите адрес'>
            <Text maxW={'150px'} truncate>
              {permissions.geolocation === 'accepted' &&
              (activePoint?.name || activePoint?.address)
                ? activePoint?.name || activePoint?.address
                : permissions.geolocation === 'pending'
                ? t('location.determining_location')
                : t('enter_address')}
            </Text>
          </Suspense>
          <DownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        maxW={'480px'}
        portalled={false}
        w={permissions.geolocation !== 'accepted' ? 'full' : '480px'}
      >
        <PopoverBody p={4}>
          {permissions.geolocation !== 'accepted' ? (
            activePoint?.address ? (
              <SavedPopover
                activePoint={activePoint}
                onClose={() => setOpen(false)}
              />
            ) : (
              <AskAddress onClose={() => setOpen(false)} />
            )
          ) : (
            <Points isOpen={open} onClose={() => setOpen(false)} />
          )}
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
}

const AskAddress = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();

  const t = useTranslations();

  return (
    <VStack textAlign='center' gap={3} bg='background.card' color='text'>
      <div>
        <Text fontSize='xl' fontWeight={700} lineHeight={1} mb='2px'>
          {t('location.enter_delivery_address')}
        </Text>
        <Text fontSize='xs'>{t('location.with_the_address')}</Text>
      </div>
      <HStack w='full'>
        <Button
          flex={1}
          rounded={16}
          size={'lg'}
          height={'56px'}
          bgColor='gray.200'
          variant='subtle'
          onClick={() => onClose()}
        >
          {t('location.later')}
        </Button>
        <Button
          flex={1}
          rounded={16}
          size={'lg'}
          height={'56px'}
          bg='primary'
          color='primary.text'
          _hover={{
            bg: 'primary.hover',
          }}
          onClick={() => {
            onClose();
            dispatch(onOpenMap());
          }}
        >
          {t('location.enter_address')}
        </Button>
      </HStack>
    </VStack>
  );
};

const SavedPopover = ({
  activePoint,
  onClose,
}: {
  activePoint: IPoint | null;
  onClose: () => void;
}) => {
  const dispatch = useDispatch();

  const t = useTranslations();

  return (
    <VStack
      w={'320px'}
      textAlign='center'
      gap={8}
      bg='background.card'
      color='text'
    >
      <Box>
        <Text>{t('location.order_to_this_address?')}</Text>
        <Text fontSize='xl' fontWeight={700} lineHeight={1} mt={4}>
          {activePoint?.address}
        </Text>
      </Box>
      <HStack w={'full'}>
        <Button
          flex={1}
          rounded={16}
          size={'lg'}
          height={'56px'}
          variant='subtle'
          onClick={() => {
            dispatch(onOpenMap());
            onClose();
          }}
        >
          {t('no')}
        </Button>
        <Button
          flex={1}
          rounded={16}
          size={'lg'}
          height={'56px'}
          bg='primary'
          color='primary.text'
          _hover={{
            bg: 'primary.hover',
          }}
          onClick={() => {
            dispatch(sessionActions.acceptPGL());
            dispatch(savePoint(activePoint));
            onClose();
          }}
        >
          {t('yes')}
        </Button>
      </HStack>
    </VStack>
  );
};
